import { Tabs, Button, Spin, Form } from "antd"
import { useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { notifications } from "notifications"
import type { FeatureFlagsConfigType, FeatureRollout } from "domains/managingJson/store/types"
import { FeatureFlagsFields } from "./common/FeatureFlagsFields"
import type { FeatureFlagFormItem } from "./common/FeatureFlagsFields"
import type { FeatureDefinition } from "./types"
import { DESKTOP_FEATURES } from "./features/desktopFeatures"
import { MOBILE_FEATURES } from "./features/mobileFeatures"

const { TabPane } = Tabs

// Assemble a persisted FeatureRollout from the flat form item, attaching optional targeting filters
// only when set so untargeted flags stay byte-identical to before. Shape mirrors buff/src/api/types.ts.
const toRollout = (def: FeatureDefinition, item?: FeatureFlagFormItem): FeatureRollout => {
  const rollout: FeatureRollout = {
    ...def,
    rolloutPercentage: item?.rolloutPercentage ?? 0,
    seed: Number(item?.seed ?? 0),
  }

  // Only persist countryFilter when at least one country is selected — an empty whitelist would
  // match no one. The UI also validates this, so this guard is defense-in-depth.
  if (item?.countryMode && item.countryMode !== "none" && item.countries?.length) {
    rollout.countryFilter = { mode: item.countryMode, countries: item.countries }
  }
  if (item?.userType && item.userType !== "none") {
    rollout.userTypeFilter = { type: item.userType }
  }

  const minDays = typeof item?.minDays === "number" ? item.minDays : undefined
  const maxDays = typeof item?.maxDays === "number" ? item.maxDays : undefined
  if (minDays !== undefined || maxDays !== undefined) {
    rollout.accountAgeFilter = {
      ...(minDays !== undefined ? { minDays } : {}),
      ...(maxDays !== undefined ? { maxDays } : {}),
    }
  }

  return rollout
}

export const FeatureFlagsConfig = observer(() => {
  const { dataStore: { managingJsonStore } } = useStore()

  const [desktopForm] = Form.useForm<{ desktop: FeatureFlagFormItem[] }>()
  const [mobileForm] = Form.useForm<{ mobile: FeatureFlagFormItem[] }>()

  const [loading, setLoading] = useState(true)
  const [configData, setConfigData] = useState<FeatureFlagsConfigType | undefined>()

  useEffect(() => {
    (async() => {
      await managingJsonStore.getData()

      const featureFlagsItem = managingJsonStore.itemTypes?.find(
        item => item.type === "featureFlags",
      )


      if (featureFlagsItem) {
        const data = featureFlagsItem.data as FeatureFlagsConfigType
        setConfigData(data)

        const desktopArray: FeatureFlagFormItem[] = DESKTOP_FEATURES.map(def => {
          const saved = data.desktop.find(feature => feature.id === def.id)

          return {
            rolloutPercentage: saved?.rolloutPercentage ?? 0,
            seed: saved?.seed ?? 0,
            savedRolloutPercentage: saved?.rolloutPercentage ?? 0,
            countryMode: saved?.countryFilter?.mode ?? "none",
            countries: saved?.countryFilter?.countries ?? [],
            userType: saved?.userTypeFilter?.type ?? "none",
            minDays: saved?.accountAgeFilter?.minDays,
            maxDays: saved?.accountAgeFilter?.maxDays,
          }
        })

        desktopForm.setFieldsValue({ desktop: desktopArray })

        const mobileArray: FeatureFlagFormItem[] = MOBILE_FEATURES.map(def => {
          const saved = data.mobile.find(x => x.id === def.id)

          return {
            rolloutPercentage: saved?.rolloutPercentage ?? 0,
            seed: saved?.seed ?? 0,
            savedRolloutPercentage: saved?.rolloutPercentage ?? 0,
            countryMode: saved?.countryFilter?.mode ?? "none",
            countries: saved?.countryFilter?.countries ?? [],
            userType: saved?.userTypeFilter?.type ?? "none",
            minDays: saved?.accountAgeFilter?.minDays,
            maxDays: saved?.accountAgeFilter?.maxDays,
          }
        })
        mobileForm.setFieldsValue({ mobile: mobileArray })
      } else {
        // Initialize with 0% for all features if no config exists
        const desktopArray: FeatureFlagFormItem[] = DESKTOP_FEATURES.map(() => ({
          rolloutPercentage: 0,
          seed: 0,
          savedRolloutPercentage: 0,
          countryMode: "none",
          countries: [],
          userType: "none",
        }))
        desktopForm.setFieldsValue({ desktop: desktopArray })

        const mobileArray: FeatureFlagFormItem[] = MOBILE_FEATURES.map(() => ({
          rolloutPercentage: 0,
          seed: 0,
          savedRolloutPercentage: 0,
          countryMode: "none",
          countries: [],
          userType: "none",
        }))
        mobileForm.setFieldsValue({ mobile: mobileArray })
      }

      setLoading(false)
    })()
  }, [managingJsonStore, desktopForm, mobileForm])

  const handleSaveAll = async() => {
    try {
      const [desktopVals, mobileVals] = await Promise.all([
        desktopForm.validateFields(),
        mobileForm.validateFields(),
      ])

      const desktop = (desktopVals.desktop ?? []).map((item, idx) => toRollout(DESKTOP_FEATURES[idx], item))
      const mobile = (mobileVals.mobile ?? []).map((item, idx) => toRollout(MOBILE_FEATURES[idx], item))

      managingJsonStore.funcAfterSuccess = async() => {
        notifications.success("Feature flags saved successfully")
        await managingJsonStore.getData()
      }

      const config = {
        name: "featureFlags",
        type: "featureFlags",
        data: { desktop, mobile },
      } as const

      if (configData) {
        await managingJsonStore.update(config)
      } else {
        await managingJsonStore.create(config)
      }

      const updatedDesktopState = (desktopVals.desktop ?? []).map(item => ({
        ...item,
        savedRolloutPercentage: item?.rolloutPercentage ?? 0,
        seedUnlocked: (item?.rolloutPercentage ?? 0) === 0 ? (item?.seedUnlocked ?? true) : false,
      }))
      const updatedMobileState = (mobileVals.mobile ?? []).map(item => ({
        ...item,
        savedRolloutPercentage: item?.rolloutPercentage ?? 0,
        seedUnlocked: (item?.rolloutPercentage ?? 0) === 0 ? (item?.seedUnlocked ?? true) : false,
      }))
      desktopForm.setFieldsValue({ desktop: updatedDesktopState })
      mobileForm.setFieldsValue({ mobile: updatedMobileState })
    } catch (error) {
      console.error("Failed to save feature flags:", error)
      notifications.error("Failed to save configuration")
    }
  }

  if (loading) return <Spin />

  return (
    <>
      <Tabs>
        <TabPane tab="Desktop Features" key="desktop" forceRender>
          <Form form={desktopForm} layout="vertical">
            <FeatureFlagsFields
              platform="Desktop"
              listName="desktop"
              features={DESKTOP_FEATURES}
            />
          </Form>
        </TabPane>

        <TabPane tab="Mobile Features" key="mobile" forceRender>
          <Form form={mobileForm} layout="vertical">
            <FeatureFlagsFields
              platform="Mobile"
              listName="mobile"
              features={MOBILE_FEATURES}
            />
          </Form>
        </TabPane>
      </Tabs>

      <Button type="primary" onClick={handleSaveAll} style={{ marginBottom: 16 }} size="large">
        Save All Tabs
      </Button>
    </>
  )
})
